import React from 'react';
import { typeStyle } from '@carvana/showroom-css-theme';
import { useIntersectionObserver } from 'usehooks-ts';
import { Warranty } from '@components/service-and-repair/constants';
import { LinkWithNativeEvents } from '../_common/link/link';
import styles from './vehicle-coverage.module.css';
import { WarrantyData } from './vehicle-coverage';
import { ExpiredLabel, WarrantyHighlight } from './shared';

export type CarvanaCareProps = {
  warranty?: WarrantyData;
  onIntersection?: () => void;
  onToggle?: (isOpen: boolean) => void;
};

export const CarvanaCare = ({ warranty: carvanaCare, onIntersection, onToggle }: CarvanaCareProps) => {
  const isCarvanaCareExpired = !!(carvanaCare === undefined || (carvanaCare && carvanaCare?.isExpired));
  const isCarvanaCosmetic = carvanaCare?.type === Warranty.CarvanaCareCosmetic;
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (isIntersecting && onIntersection) onIntersection();
    },
  });

  if (!carvanaCare) {
    return null;
  }

  return (
    <>
      <div className={styles['divider']} />

      <div ref={ref} className={styles['carvana-care']}>
        <div>
          <div style={typeStyle('header-s')} className={styles['header']}>
            CarvanaCare&reg;{' '}
            <span style={typeStyle('body-m')} data-testid="carvana-care-type">
              {carvanaCare?.type.replace(/CarvanaCare/i, ' ')}
            </span>
            <ExpiredLabel isExpired={isCarvanaCareExpired} />
          </div>
        </div>

        <p style={typeStyle('body-s')}>
          {(() => {
            switch (carvanaCare?.type) {
              case Warranty.CarvanaCareCosmetic:
                return (
                  <>
                    CarvanaCare, which you&apos;ve purchased, provides paintless dent repairs, tire and wheel repairs,
                    plus oil changes and tire rotations for the first year of ownership.{' '}
                    <span data-testid="carvana-care-deductible-innetwork">
                      ${carvanaCare.deductible?.inNetwork || 0}
                    </span>{' '}
                    deductible at in-network shops.
                  </>
                );
              default:
                return (
                  <>
                    CarvanaCare&reg;, which you&apos;ve purchased, provides basic mechanical coverage for your engine
                    and transmission.{' '}
                    <span data-testid="carvana-care-deductible-innetwork">
                      ${carvanaCare.deductible?.inNetwork || 0}
                    </span>{' '}
                    deductible at in-network shops and{' '}
                    <span data-testid="carvana-care-deductible-outnetwork">
                      ${carvanaCare.deductible?.outOfNetwork || 0}
                    </span>{' '}
                    at out-of-network shops.
                  </>
                );
            }
          })()}
        </p>

        {carvanaCare?.brochureUrl && (
          <LinkWithNativeEvents
            style={typeStyle('body-s')}
            href={carvanaCare?.brochureUrl}
            target="_blank"
            rel="noreferrer"
            nativeEvent={{
              event: 'link',
              payload: { url: carvanaCare?.brochureUrl },
            }}
          >
            See full list of coverage
          </LinkWithNativeEvents>
        )}

        <WarrantyHighlight
          isExpired={isCarvanaCareExpired}
          durationLeft={carvanaCare.durationLeft}
          totalCoverageInMiles={carvanaCare.totalCoverageInMiles}
          id="carvana-care"
          totalCoverageInMilesAlternativeText={isCarvanaCosmetic ? 'unlimited miles' : undefined}
        />
      </div>
    </>
  );
};
